/**
 * Componente de gráfico donut para total de pedidos
 * Donut chart component for total orders
 */

import { useMemo } from 'react';
import { SectionBox } from '@shared/components/ui/SectionBox';
import { useTranslation } from 'react-i18next';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { useState, useRef, useEffect } from 'react';
import { GlobalMetrics } from '../../models/LogisticsApiResponse';

interface TotalOrdersDonutChartProps {
  globalMetrics?: GlobalMetrics;
  isLoading?: boolean;
}

export const TotalOrdersDonutChart = ({ globalMetrics }: TotalOrdersDonutChartProps) => {
  const { t } = useTranslation('logisticsModule');
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  // Calcular datos del gráfico desde globalMetrics
  // Calculate chart data from globalMetrics
  const { total, chartData } = useMemo(() => {
    if (!globalMetrics) {
      return {
        total: 0,
        chartData: [
          {
            name: t('orders_by_period.tabs.sent'),
            value: 0,
            color: '#296dff', // Azul para enviados - Blue for sent
            percentage: 0,
          },
          {
            name: t('orders_by_period.tabs.delivered'),
            value: 0,
            color: '#79E4D9', // Turquesa para entregados - Turquoise for delivered
            percentage: 0,
          },
          {
            name: t('orders_by_period.tabs.in_transit'),
            value: 0,
            color: '#4dd0e1', // Azul celeste claro para en tránsito - Light cyan blue for in transit
            percentage: 0,
          },
          {
            name: t('orders_by_period.tabs.returned'),
            value: 0,
            color: '#26c6da', // Azul celeste medio para devueltos - Medium cyan blue for returned
            percentage: 0,
          },
        ],
      };
    }

    const totalValue = globalMetrics.total;
    const sentValue = globalMetrics.sent;
    const deliveredValue = globalMetrics.delivered;
    const inTransitValue = globalMetrics.in_transit;
    const returnedValue = globalMetrics.returned;

    const sentPercentage = totalValue > 0 ? (sentValue / totalValue) * 100 : 0;
    const deliveredPercentage = totalValue > 0 ? (deliveredValue / totalValue) * 100 : 0;
    const inTransitPercentage = totalValue > 0 ? (inTransitValue / totalValue) * 100 : 0;
    const returnedPercentage = totalValue > 0 ? (returnedValue / totalValue) * 100 : 0;

    return {
      total: totalValue,
      chartData: [
        {
          name: t('orders_by_period.tabs.sent'),
          value: sentValue,
          color: '#296dff', // Azul para enviados - Blue for sent
          percentage: sentPercentage,
        },
        {
          name: t('orders_by_period.tabs.delivered'),
          value: deliveredValue,
          color: '#79E4D9', // Turquesa para entregados - Turquoise for delivered
          percentage: deliveredPercentage,
        },
        {
          name: t('orders_by_period.tabs.in_transit'),
          value: inTransitValue,
          color: '#4dd0e1', // Azul celeste claro para en tránsito - Light cyan blue for in transit
          percentage: inTransitPercentage,
        },
        {
          name: t('orders_by_period.tabs.returned'),
          value: returnedValue,
          color: '#26c6da', // Azul celeste medio para devueltos - Medium cyan blue for returned
          percentage: returnedPercentage,
        },
      ],
    };
  }, [globalMetrics, t]);

  // Referencia al contenedor para calcular el tamaño dinámicamente
  // Container reference to calculate size dynamically
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerSize, setContainerSize] = useState(320);

  // Tamaño base y radios base para desktop
  // Base size and base radii for desktop
  const chartSizeBase = 320;
  const innerRadiusBase = 85;
  const outerRadiusBase = 140;

  // Calcular radios proporcionales al tamaño del contenedor
  // Calculate proportional radii based on container size
  useEffect(() => {
    if (!containerRef.current) return;

    const updateSize = () => {
      if (containerRef.current) {
        const width = containerRef.current.offsetWidth;
        setContainerSize(width);
      }
    };

    // Actualizar tamaño inicial
    // Update initial size
    updateSize();

    // Usar ResizeObserver para detectar cambios de tamaño del contenedor
    // Use ResizeObserver to detect container size changes
    const resizeObserver = new ResizeObserver(() => {
      updateSize();
    });

    resizeObserver.observe(containerRef.current);

    // También escuchar cambios de tamaño de la ventana como fallback
    // Also listen to window resize as fallback
    window.addEventListener('resize', updateSize);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateSize);
    };
  }, []);

  // Calcular radios proporcionales
  // Calculate proportional radii
  const innerRadius = Math.round((innerRadiusBase / chartSizeBase) * containerSize);
  const outerRadius = Math.round((outerRadiusBase / chartSizeBase) * containerSize);

  // Custom tooltip
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-card-bg border border-card-border rounded-md shadow-lg p-3 z-50">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: data.color }} />
            <div>
              <p className="font-medium text-text">{data.name}</p>
              <p className="text-sm text-subtext">
                {data.value} ({data.percentage.toFixed(1)}%)
              </p>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <SectionBox data-testid="total-orders-donut-chart" className="h-full overflow-visible">
      {/* Título - Title */}
      <h2 className="text-lg font-semibold text-title mb-4">{t('total_orders.title')}</h2>

      {/* Gráfico donut - Donut chart */}
      <div className="flex flex-col items-center justify-center min-h-[200px] sm:min-h-[280px] py-4 sm:py-6 px-4 sm:px-6 overflow-visible">
        <div
          ref={containerRef}
          className="relative w-full max-w-[200px] sm:max-w-[320px] mx-auto overflow-visible aspect-square"
        >
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                innerRadius={innerRadius} // Radio interno calculado proporcionalmente
                outerRadius={outerRadius} // Radio externo calculado proporcionalmente
                paddingAngle={0}
                dataKey="value"
                isAnimationActive={false}
                stroke="none"
                onMouseEnter={(_, index) => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(null)}
              >
                {chartData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={entry.color}
                    stroke="none"
                    style={{
                      transform: hoveredIndex === index ? 'scale(1.03)' : 'scale(1)',
                      transformOrigin: 'center',
                      transition: 'transform 0.2s ease-in-out',
                    }}
                  />
                ))}
              </Pie>
              <Tooltip
                content={<CustomTooltip />}
                offset={30}
                position={{ x: 0, y: -60 }}
                wrapperStyle={{ pointerEvents: 'none' }}
              />
            </PieChart>
          </ResponsiveContainer>

          {/* Contenido central - Center content */}
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none z-0">
            <span className="text-lg font-bold text-title">{total}</span>
          </div>
        </div>
      </div>
    </SectionBox>
  );
};
